import { PlusCircleTwoTone } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Col, Divider, List, message, Modal, Row } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, DatePicker, SearchInput } from '../../../components';
import { TIER_MAPPINGS } from '../../../constants';
import { withRouter } from '../../../utils/wrappers';
import { getClientDetail } from '../client-details.action';
import { clientService } from '../client-details.service';

const StyledModal = styled.div`
  em.label {
    font-family: AvenirLTStdHeavy;
    font-size: 14px;
    color: #707070;
    display: block;
    font-style: normal;
    margin-bottom: 5px;
  }
`;

const MappedTiers = ({ params, mappedTiers = [] }) => {
  const dispatch = useDispatch();
  const { clientID } = params;
  const [tier, setTier] = useState(undefined);
  const [startDate, setStartDate] = useState(undefined);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [viewAllTiers, setViewAllTiers] = useState(false);
  const [latestStartDate, setLatestStartDate] = useState(moment().subtract(1, 'days'));

  const disabledDate = (current) => {
    if (current > latestStartDate) {
      return current && current < moment().subtract(1, 'days').endOf('day');
    }

    return current && current < latestStartDate.endOf('day');
  };

  const handleTier = (value) => {
    setTier(value);
  };

  const handleStartDate = (value) => {
    setStartDate(value);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const handleAdd = () => {
    const key = `tier-${new Date().getTime()}`;
    message.loading({ content: 'Processing...', key });
    setLoading(true);

    if (!tier || !startDate) {
      setLoading(false);
      message.warn({ content: 'Please add tier and start date', key });
      return;
    }

    const body = {
      tier: { tier, start_date: startDate.format('YYYY-MM-DDT00:00:00') + 'Z' }
    };

    clientService
      .editClientConfig(clientID, body)
      .then(() => {
        setLoading(false);
        setVisible(false);
        message.success({ content: 'Added!', key });
        dispatch(getClientDetail(clientID));
      })
      .catch((err) => {
        setLoading(false);
        message.error({ content: 'Failed to process', key });
        console.error(err);
      });
  };

  const afterClose = () => {
    setTier(undefined);
    setStartDate(undefined);
  };

  useEffect(() => {
    if (mappedTiers.length) {
      setTiers(mappedTiers.slice(0, 5));
      setLatestStartDate(moment.utc(mappedTiers[0].start_date));
    }
  }, [mappedTiers]);

  useEffect(() => {
    if (viewAllTiers) {
      setTiers(mappedTiers);
    } else {
      setTiers(mappedTiers.slice(0, 5));
    }
  }, [viewAllTiers]);

  return (
    <>
      <Modal
        title="Add Tier"
        visible={visible}
        onOk={handleAdd}
        onCancel={handleCancel}
        destroyOnClose={true}
        afterClose={afterClose}
        footer={[
          <Button key="back" type="secondary" disabled={loading} onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" loading={loading} onClick={handleAdd}>
            Add
          </Button>
        ]}
      >
        <StyledModal>
          <em className="label">Tier</em>
          <SearchInput
            placeholder="Type or select..."
            id="tier-mapping"
            items={TIER_MAPPINGS}
            value={tier}
            onChange={handleTier}
          />
          <br />
          <em className="label">Start Date</em>
          <DatePicker
            id="start_date"
            value={startDate}
            onChange={handleStartDate}
            disabledDate={disabledDate}
          />
        </StyledModal>
      </Modal>

      <Row gutter={[24, 16]}>
        <Col span={24}>
          <h5>
            Mapped Tier &nbsp;
            <Button
              type="link"
              icon={<PlusCircleTwoTone twoToneColor="#5050f9" />}
              onClick={() => setVisible(true)}
            />
          </h5>
          <List
            header={
              <Row gutter={[24, 16]}>
                <Col span={6}>
                  <span className="header">Tier</span>
                </Col>
                <Col span={6}>
                  <span className="header">Currency</span>
                </Col>
                <Col span={6}>
                  <span className="header">Start Date</span>
                </Col>
                <Col span={6}>
                  <span className="header">End Date</span>
                </Col>
              </Row>
            }
            footer={
              (tiers.length !== mappedTiers.length || mappedTiers.length > 5) && (
                <div style={{ textAlign: 'center' }}>
                  <>
                    {!viewAllTiers && (
                      <span className="view-link" onClick={() => setViewAllTiers(true)}>
                        View All
                      </span>
                    )}
                    {viewAllTiers && (
                      <span className="view-link" onClick={() => setViewAllTiers(false)}>
                        View Less
                      </span>
                    )}
                  </>
                </div>
              )
            }
            bordered
            size="small"
            dataSource={tiers}
            renderItem={(item) => (
              <Row gutter={[24, 16]} style={{ padding: '8px 15px' }}>
                <Col span={6}>
                  <span className="value">{item.tier}</span>
                </Col>
                <Col span={6}>
                  <span className="value">{item.currency}</span>
                </Col>
                <Col span={6}>
                  <span className="value">
                    {item.start_date ? moment.utc(item.start_date).format('DD MMM YYYY') : '-'}
                  </span>
                </Col>
                <Col span={6}>
                  <span className="value">
                    {item.end_date ? moment.utc(item.end_date).format('DD MMM YYYY') : '-'}
                  </span>
                </Col>
              </Row>
            )}
          />

          <Divider />
        </Col>
      </Row>
    </>
  );
};

export default withRouter(MappedTiers);
